import { useMemo } from 'react';
import { MdOutlineAltRoute, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { Accordion, AccordionItem } from '@ds';
import { CopyToClipboard } from '@ui';
import { TripAssignmentCard, TripCheckpoint, TripStatusChip } from '@lp/features/RoutePlanner/components/GroundTrip';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { CheckpointTypeEnum } from '@/api/apis/drive-service';
import { useDriveTripQuery } from '@/queries/drive-service';
import { useWatchFields } from '@/hooks/useWatchFields';
import { getFullName } from '@/utils/helpers';
import { TripInfoBlock } from './TripInfoBlock';

type GroundTripInfoProps = {
  tripId: string;
};

export const GroundTripInfo = ({ tripId }: GroundTripInfoProps) => {
  const { departureDateTime, arrivalDateTime } = useWatchFields<CommonRouteLegSchemaValues>([
    'departureDateTime',
    'arrivalDateTime',
  ] as const);
  const { data } = useDriveTripQuery(tripId);

  const tripPlanContent = useMemo(() => {
    if (!data?.tripPlan?.checkpoints?.length) return [];

    return data.tripPlan.checkpoints.map((item) => {
      const id =
        item.$type === CheckpointTypeEnum.PlannedCheckpointResponse ? item.checkpointId : item.modeData.waitStartTime;

      return {
        id,
        item: (
          <TripCheckpoint
            data={item}
            tripPassengers={data.passengers || []}
            departureDateTime={departureDateTime}
            arrivalDateTime={arrivalDateTime}
            isMergedTrip={data.isMergedTrip}
          />
        ),
      };
    });
  }, [data?.tripPlan, data?.passengers, departureDateTime, arrivalDateTime, data?.isMergedTrip]);

  const tripAssigneeContent = useMemo(() => {
    return [
      ...(data?.affiliatedOrganization
        ? [
            {
              id: data.affiliatedOrganization.assignmentStatus,
              item: (
                <TripAssignmentCard
                  type="ao"
                  title={data.affiliatedOrganization.affiliatedOrganization?.name || 'Affiliated Organization'}
                  subtitle={data.affiliatedOrganization.affiliatedOrganization?.phone || ''}
                  status={data.affiliatedOrganization.assignmentStatus}
                />
              ),
            },
          ]
        : []),
      ...(data?.driver
        ? [
            {
              id: data.driver.assignmentStatus,
              item: (
                <TripAssignmentCard
                  type="driver"
                  title={getFullName(data.driver.driver?.firstName, data.driver.driver?.lastName) || 'Driver'}
                  subtitle={data.driver.driver?.phone || ''}
                  status={data.driver.assignmentStatus}
                  image={data.driver.driver?.imagePath}
                />
              ),
            },
          ]
        : []),
      ...(data?.vehicle
        ? [
            {
              id: data.vehicle.id,
              item: (
                <TripAssignmentCard
                  type="vehicle"
                  title={getFullName(data.vehicle.make, data.vehicle.model) || 'Vehicle'}
                  subtitle={data.vehicle.licensePlateNumber || ''}
                />
              ),
            },
          ]
        : []),
    ];
  }, [data?.affiliatedOrganization, data?.driver, data?.vehicle]);

  const tripSpecialContent = useMemo(() => {
    return [
      ...(data?.specialOptions?.lightsAndSirens
        ? [
            {
              id: 'lightsAndSirens',
              item: <span className="text-s-m text-base-5">Lights and Sirens: Yes</span>,
            },
          ]
        : []),
      ...(data?.specialOptions?.comment
        ? [
            {
              id: 'comment',
              item: <span className="text-s-m text-base-5">{data.specialOptions.comment}</span>,
            },
          ]
        : []),
    ];
  }, [data?.specialOptions]);

  if (!data) return null;

  return (
    <Accordion className="p-0 w-auto!" isCompact defaultExpandedKeys={[tripId]} showDivider={false}>
      <AccordionItem
        classNames={{
          base: 'bg-white rounded-lg gap-6',
          trigger: 'cursor-pointer p-4',
          indicator: 'text-base-20 data-[open=true]:-rotate-180',
          content: 'px-4 pb-4 flex flex-col gap-6',
        }}
        title={
          <div className="flex gap-2 items-center">
            {data.isMergedTrip && <MdOutlineAltRoute size={20} />}
            <span className="text-l-sb text-base-5">Ground Trip #{data.tripNumber}</span>
            <CopyToClipboard
              text={data.tripNumber}
              confirmationMessage="Trip number copied to clipboard"
              iconSize={18}
            />
            <TripStatusChip status={data.status} />
          </div>
        }
        textValue={data.tripNumber}
        indicator={<MdOutlineKeyboardArrowDown size={24} />}
      >
        <TripInfoBlock title="Trip Plan" emptyMessage="No trip plan yet" content={tripPlanContent} />
        <TripInfoBlock
          title="Assigned to"
          emptyMessage="No resources assigned yet"
          content={tripAssigneeContent}
          listType="horizontal"
        />
        <TripInfoBlock
          title="Special Instructions"
          emptyMessage="No special instructions added yet"
          content={tripSpecialContent}
        />
      </AccordionItem>
    </Accordion>
  );
};
